/**
 * EloBadge — tier-colored ELO display chip.
 *
 * Sizes:
 *   'nano'   — top bar / inline (just the number, very compact)
 *   'small'  — lobby rows, match history table cells
 *   'medium' — match player cards, friend rows
 *   'large'  — profile hero (bracket label shown, bigger numeral)
 *
 * The tier label below the number is hidden by default across all sizes.
 * Pass `showTierLabel` explicitly to opt in — the tooltip always shows tier info on hover.
 *
 * The Orbitron font is already loaded globally via renderFullPage.js.
 */
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import { getEloTier, formatElo } from './eloTierUtils';
import { colors } from '../../theme/colors';

/** Size-variant config. Values may be MUI breakpoint objects for mobile scale-down. */
const SIZE_CONFIG = {
    nano: {
        px: { xs: '0.36rem', sm: '0.52rem' },
        py: { xs: '0.14rem', sm: '0.3rem' },
        numberSize: { xs: '0.68rem', sm: '0.82rem' },
        labelSize: { xs: '0.46rem', sm: '0.52rem' },
        borderRadius: { xs: '4px', sm: '5px' },
        borderWidth: '1px',
        gap: '0',
        showTierLabelDefault: false,
        letterSpacing: '0.04em',
    },
    small: {
        px: { xs: '0.46rem', sm: '0.62rem' },
        py: { xs: '0.16rem', sm: '0.3rem' },
        numberSize: { xs: '0.72rem', sm: '0.84rem' },
        labelSize: { xs: '0.5rem', sm: '0.56rem' },
        borderRadius: { xs: '5px', sm: '6px' },
        borderWidth: '1px',
        gap: '0.08rem',
        showTierLabelDefault: false,
        letterSpacing: '0.04em',
    },
    medium: {
        px: { xs: '0.5rem', sm: '0.65rem' },
        py: { xs: '0.16rem', sm: '0.26rem' },
        numberSize: { xs: '0.86rem', sm: '1rem' },
        labelSize: { xs: '0.54rem', sm: '0.6rem' },
        borderRadius: { xs: '6px', sm: '7px' },
        borderWidth: '1px',
        gap: '0.1rem',
        showTierLabelDefault: false,
        letterSpacing: '0.05em',
    },
    large: {
        px: { xs: '0.75rem', sm: '1rem' },
        py: { xs: '0.3rem', sm: '0.45rem' },
        numberSize: { xs: '1.2rem', sm: '1.5rem' },
        labelSize: { xs: '0.6rem', sm: '0.68rem' },
        borderRadius: { xs: '7px', sm: '9px' },
        borderWidth: { xs: '1px', sm: '1.5px' },
        gap: '0.18rem',
        showTierLabelDefault: false,
        letterSpacing: '0.06em',
    },
};

/**
 * Badge silhouette: rounded corners + tiny top-right bite + V left incision.
 * Left notch is a carved V (\ /), not a flat triangular punch.
 */
const BADGE_CRACK_CLIP = `
  polygon(
    0 6px,
    1.2px 2.4px,
    6px 0,
    calc(100% - 14px) 0,
    calc(100% - 12px) 3px,
    calc(100% - 10px) 0,
    calc(100% - 6px) 0,
    calc(100% - 1.2px) 2.4px,
    100% 6px,
    100% calc(100% - 6px),
    calc(100% - 1.2px) calc(100% - 2.4px),
    calc(100% - 6px) 100%,
    6px 100%,
    1.2px calc(100% - 2.4px),
    0 calc(100% - 6px),
    0 58%,
    3px 53%,
    6px 50%,
    3px 47%,
    0 42%
  )
`;

/**
 * Inner badge box — extracted so Tooltip can wrap it.
 * forwardRef is needed for MUI Tooltip to attach its ref.
 */
const ELoBadgeInner = React.forwardRef(function EloBadgeInner(
    { tier, displayValue, sizeConf, showTierLabel, style, className, ...other },
    ref
) {
    return (
        <Box
            ref={ref}
            className={className}
            style={style}
            {...other}
            sx={{
                position: 'relative',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: sizeConf.gap,
                px: sizeConf.px,
                py: sizeConf.py,
                borderRadius: 0,
                clipPath: BADGE_CRACK_CLIP,
                WebkitClipPath: BADGE_CRACK_CLIP,
                backgroundColor: tier.bg,
                // Soft lighting + micro brush + anodized mid-band (darker center)
                backgroundImage: `
                    linear-gradient(
                        115deg,
                        rgba(255,255,255,.08) 0%,
                        rgba(255,255,255,.02) 30%,
                        transparent 58%,
                        rgba(0,0,0,.1) 84%,
                        rgba(0,0,0,.26) 100%
                    ),
                    repeating-linear-gradient(
                        90deg,
                        rgba(255,255,255,.012),
                        rgba(255,255,255,.012) 1px,
                        transparent 1px,
                        transparent 5px
                    ),
                    linear-gradient(
                        180deg,
                        ${alpha('#FFFFFF', 0.1)} 0%,
                        ${tier.bg} 18%,
                        color-mix(in srgb, ${tier.bg} 72%, #000) 48%,
                        ${tier.bg} 70%,
                        ${alpha('#000000', 0.4)} 100%
                    )
                `,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,.14),
                    inset 0 3px 6px rgba(255,255,255,.04),
                    inset 0 -3px 8px rgba(0,0,0,.34),
                    inset 0 0 0 1px rgba(255,255,255,.03),
                    0 3px 10px rgba(0,0,0,.32)
                `,
                borderStyle: 'solid',
                borderColor: alpha(tier.border, 0.88),
                borderWidth: sizeConf.borderWidth,
                outline: `1px solid ${alpha('#FFF', 0.04)}`,
                outlineOffset: 0,
                boxSizing: 'border-box',
                flexShrink: 0,
                userSelect: 'none',
                overflow: 'hidden',
                transition:
                    'transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 1,
                    right: 1,
                    top: 1,
                    height: '58%',
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(255,255,255,.12) 0%,
                            rgba(255,255,255,.05) 28%,
                            rgba(255,255,255,.015) 58%,
                            transparent 100%
                        )
                    `,
                    pointerEvents: 'none',
                    zIndex: 0,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '10%',
                    right: '10%',
                    bottom: 1,
                    height: 1,
                    background: `linear-gradient(
                        90deg,
                        transparent,
                        ${alpha(tier.text, 0.4)} 20%,
                        ${alpha('#FFF', 0.18)} 50%,
                        ${alpha(tier.text, 0.4)} 80%,
                        transparent
                    )`,
                    opacity: 0.75,
                    pointerEvents: 'none',
                    zIndex: 0,
                },
                // Near-invisible inner shade on the V incision walls
                '& .elo-notch': {
                    position: 'absolute',
                    left: 0,
                    top: '42%',
                    width: 7,
                    height: '16%',
                    background:
                        'linear-gradient(90deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,.18) 55%, transparent 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.08)',
                    clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                },
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,.18),
                        inset 0 3px 7px rgba(255,255,255,.05),
                        inset 0 -4px 10px rgba(0,0,0,.4),
                        inset 0 0 0 1px rgba(255,255,255,.04),
                        0 8px 18px rgba(0,0,0,.4)
                    `,
                },
            }}
        >
            <Box className="elo-notch" aria-hidden />
            <Typography
                component="span"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: sizeConf.numberSize,
                    fontWeight: 800,
                    color: tier.text,
                    lineHeight: 1.2,
                    letterSpacing: sizeConf.letterSpacing,
                    whiteSpace: 'nowrap',
                    textShadow: `
                        0 1px rgba(255,255,255,.08),
                        0 -1px rgba(0,0,0,.25)
                    `,
                }}
            >
                {displayValue}
            </Typography>
            {showTierLabel && (
                <Typography
                    component="span"
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        fontFamily: "'Orbitron', sans-serif",
                        fontSize: sizeConf.labelSize,
                        fontWeight: 600,
                        color: tier.text,
                        opacity: 0.78,
                        lineHeight: 1,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {tier.label}
                </Typography>
            )}
        </Box>
    );
});

ELoBadgeInner.displayName = 'EloBadgeInner';

/**
 * Tooltip content shown on hover — tier title + modular info rows.
 * On xs, label stacks above value so long ranges never collide.
 */
function EloTooltipInfoRow({ label, value }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: '0.18rem', sm: '0.75rem' },
                padding: { xs: '0.38rem 0.48rem', sm: '0.42rem 0.55rem' },
                borderRadius: { xs: '6px', sm: '8px' },
                background: alpha('#FFF', 0.025),
                border: '1px solid rgba(255,255,255,.04)',
            }}
        >
            <Typography
                sx={{
                    fontSize: { xs: '0.48rem', sm: '0.55rem' },
                    fontWeight: 700,
                    letterSpacing: { xs: '.06em', sm: '.08em' },
                    textTransform: 'uppercase',
                    color: colors.text.muted,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: '0.62rem', sm: '0.68rem' },
                    color: colors.text.primary,
                    whiteSpace: 'nowrap',
                    textAlign: { xs: 'left', sm: 'right' },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function EloTooltipContent({ tier, value }) {
    const rangeText =
        tier.max === Infinity
            ? `${tier.min}+`
            : `${tier.min}–${tier.max}`;
    const eloText =
        value != null && Number.isFinite(Number(value))
            ? Math.round(Number(value)).toLocaleString()
            : '—';

    return (
        <Box
            sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 0, sm: '160px' },
                maxWidth: { xs: '100%', sm: '220px' },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '0.35rem', sm: '0.45rem' },
            }}
        >
            <Box
                sx={{
                    height: { xs: '2px', sm: '3px' },
                    borderRadius: '999px',
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            ${tier.border},
                            transparent
                        )
                    `,
                }}
            />
            <Typography
                sx={{
                    textAlign: 'center',
                    fontFamily: "'Orbitron', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: '0.62rem', sm: '0.72rem' },
                    letterSpacing: { xs: '.08em', sm: '.1em' },
                    textTransform: 'uppercase',
                    color: tier.text,
                }}
            >
                {tier.label}
            </Typography>
            <Box
                sx={{
                    height: 1,
                    background: alpha('#FFF', 0.05),
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: '0.28rem', sm: '0.35rem' },
                }}
            >
                <EloTooltipInfoRow label="Current ELO" value={eloText} />
                <EloTooltipInfoRow label="Tier Range" value={rangeText} />
            </Box>
        </Box>
    );
}

export default function EloBadge({
    value,
    displayValue: displayValueProp,
    size = 'medium',
    showTierLabel,
    tooltip = true,
    style,
    className,
}) {
    const tier = getEloTier(value);
    const displayValue =
        displayValueProp != null ? displayValueProp : formatElo(value);
    const sizeConf = SIZE_CONFIG[size] || SIZE_CONFIG.medium;
    const resolvedShowTierLabel =
        showTierLabel !== undefined ? showTierLabel : sizeConf.showTierLabelDefault;

    const badge = (
        <ELoBadgeInner
            tier={tier}
            displayValue={displayValue}
            sizeConf={sizeConf}
            showTierLabel={resolvedShowTierLabel}
            style={style}
            className={className}
        />
    );

    if (!tooltip) return badge;

    return (
        <Tooltip
            title={<EloTooltipContent tier={tier} value={value} />}
            placement="top"
            slotProps={{
                tooltip: {
                    sx: {
                        background: `
                            linear-gradient(
                                180deg,
                                #141518 0%,
                                #0C0D0F 100%
                            )
                        `,
                        border: `1px solid ${alpha(tier.border, 0.55)}`,
                        borderRadius: { xs: '8px', sm: '7px' },
                        p: { xs: '0.55rem', sm: '0.8rem' },
                        maxWidth: { xs: 'min(168px, calc(100vw - 28px))', sm: 240 },
                        boxShadow: `
                            0 20px 40px rgba(0,0,0,.45),
                            inset 0 1px rgba(255,255,255,.04)
                        `,
                    },
                },
            }}
        >
            {badge}
        </Tooltip>
    );
}

EloBadge.propTypes = {
    /** Raw ELO number (or string coercible to a number). */
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Optional display override (e.g. animated count-up) while tier still comes from `value`. */
    displayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Visual size variant. */
    size: PropTypes.oneOf(['nano', 'small', 'medium', 'large']),
    /** Override whether the tier label text is shown below the number. */
    showTierLabel: PropTypes.bool,
    /** Whether to wrap with an info tooltip on hover. */
    tooltip: PropTypes.bool,
    /** Optional inline style for the outer Box. */
    style: PropTypes.object,
    className: PropTypes.string,
};
